<template>
  <section class="lg:max-w-[600px] lg:mx-auto">
    <StatusBar v-if="!isDesktopFlow" />
    <section :class="{ 'px-4': !isDesktopFlow }">
      <ContactInfoSummary
        class="border-linear-g-primary mb-6"
        :class="isDesktopFlow ? 'border-b pb-6' : 'border-y py-6'"
      />

      <h1 v-if="isStripe" class="text-2xl md:text-4xl font-bold font-heading">Payment</h1>
      <div v-if="isStripe" class="my-6" id="payment-element" />
      <div
        v-if="errorData.message"
        id="payment-error"
        class="p-2 mt-4 rounded border border-red-400 bg-red-200 text-red-600"
      >
        {{ errorData.message }}
      </div>

      <div v-if="!isDesktopFlow" class="price-information">
        <CheckoutDetailTable />
        <CheckoutPromoCode class="my-8" />
      </div>

      <CheckoutBottomBar
        :nextButtonText="saveButtonText"
        :isNextButtonDisabled="isPaying"
        @click:next="submitPayment"
      />
    </section>
  </section>
</template>

<script lang="ts" setup>
import type {
  Appearance,
  PaymentIntentResult,
  Stripe,
  StripeElements,
  StripeElementsOptionsClientSecret,
} from '@stripe/stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useCssVar } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'

import ContactInfoSummary from '@/modules/guestUi/components/molecules/ContactInfoSummary.vue'
import CheckoutBottomBar from '@/modules/guestUi/components/organisms/CheckoutBottomBar.vue'
import CheckoutDetailTable from '@/modules/guestUi/components/organisms/CheckoutDetailTable.vue'
import CheckoutPromoCode from '@/modules/guestUi/components/organisms/CheckoutPromoCode.vue'
import StatusBar from '@/modules/guestUi/components/organisms/StatusBar.vue'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'
import { ErrorData, blankError, clearError, displayHandled } from '@/utils/errors'

const settingStore = useSettingStore()
const { public_key, stripe_account } = storeToRefs(settingStore)

const checkoutStore = useCheckoutStore()
const { client_secret, is_timer_expired, isDesktopFlow } = storeToRefs(checkoutStore)

const GUEST_UI_QUERY = '.guest-ui'
const guestUIContainer = ref<HTMLDivElement | null>(null)
onMounted(() => {
  guestUIContainer.value = document.querySelector(GUEST_UI_QUERY)
})
const brandColor = useCssVar('--primary-theme-color')
const textColorOnBrand = useCssVar('--colors-button-label', guestUIContainer)
const inputBgColor = useCssVar('--colors-input', guestUIContainer)
const inputTextColor = useCssVar('--colors-fg-g-primary', guestUIContainer)
const labelTextColor = useCssVar('--colors-fg-disclaimer', guestUIContainer)
const placeholderTextColor = useCssVar('--color-gray-60')
const primaryTextFontFamily = useCssVar('--fontFamily-sans', guestUIContainer)
const primaryTextFontFamilyUrl = useCssVar('--primary-text-font-family-url')
const borderColor = useCssVar('--colors-linear-g-primary', guestUIContainer)
const buttonStyle = { boxShadow: 'none', borderColor: borderColor.value, fontWeight: 'normal' }
const focusBorderColor = useCssVar('--g-fg-primary', null, { initialValue: 'black' })
const focusStyle = {
  boxShadow: `0 0 0 3px ${focusBorderColor.value}`,
  borderColor: 'transparent',
}
const elementsOptions = computed<StripeElementsOptionsClientSecret>(() => {
  return {
    clientSecret: client_secret.value!,
    fonts: [{ src: primaryTextFontFamilyUrl.value || '' }],
    appearance: {
      variables: {
        colorPrimary: brandColor.value,
        colorBackground: inputBgColor.value,
        colorText: inputTextColor.value,
        colorTextPlaceholder: `hsl(${placeholderTextColor.value})`,
        borderRadius: '4px',
        fontFamily: primaryTextFontFamily.value,
      },
      rules: {
        '.Tab': buttonStyle,
        '.Tab:focus': focusStyle,
        '.Tab--selected': {
          backgroundColor: brandColor.value,
          color: textColorOnBrand.value,
          borderColor: brandColor.value,
        },
        '.Tab--selected:focus': focusStyle,
        '.TabIcon--selected': { fill: textColorOnBrand.value },
        '.Input': buttonStyle,
        '.Input:focus': focusStyle,
        '.Label': { color: labelTextColor.value },
      },
    } as Appearance,
  }
})

const stripe = ref<Stripe | null>(null)
const elements = ref<StripeElements | null>(null)
const isStripe = computed<boolean>(() => {
  return checkoutStore.pricing.total > 0.5 && public_key.value !== 'pk_test_xxx'
})
const errorData = ref<ErrorData>(blankError())

async function loadStripeForm() {
  await settingStore.loading
  // We want to avoid attempting to load stripe when we know we are running a test
  if (!isStripe.value) return

  stripe.value = await loadStripe(public_key.value, { stripeAccount: stripe_account.value })
  if (!stripe.value) return

  elements.value = stripe.value.elements(elementsOptions.value)
  if (!elements.value) return
  const paymentElement = elements.value.create('payment')
  paymentElement.mount('#payment-element')
}

onMounted(loadStripeForm)

const isPaying = ref<boolean>(false)
async function submitPayment() {
  if (is_timer_expired.value) return
  try {
    isPaying.value = true
    clearError(errorData.value)

    let stripeResponse: PaymentIntentResult | undefined
    if (checkoutStore.requiresPayment) {
      await elements.value?.submit()
      stripeResponse = await stripe.value?.confirmPayment({
        elements: elements.value!,
        clientSecret: client_secret.value!,
        redirect: 'if_required',
        confirmParams: { return_url: window.location.href },
      })
    }
    await checkoutStore.checkout(stripeResponse)
  } catch (error) {
    displayHandled(error, errorData.value)
  } finally {
    isPaying.value = false
  }
}
const saveButtonText = computed<string>(() => {
  if (isPaying.value) return 'Processing...'
  return 'Purchase'
})
</script>
